import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Lock, FileDown, AlertTriangle, Clock, Building2, Eye, RefreshCw, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USAGE_LABELS = {
  TRAITEMENT_SURFACE: "Traitement parcelle/surface",
  ESPACE_FERME: "Traitement espace fermé",
  TRAITEMENT_SEMENCES_PLANTS: "Traitement semences/plants",
  SEMIS_SEMENCES_TRAITEES: "Semis semences traitées",
  PLANTATION: "Plantation",
  RECOLTE: "Récolte",
}
const isEvent = (e) => e.usage_type === "PLANTATION" || e.usage_type === "RECOLTE";;

const STATUS_META = {
  draft: { label: "Brouillon", bg: "#E5E7EB", color: "#374151" },
  validated: { label: "Validée", bg: "#DBEAFE", color: "#1E40AF" },
  locked: { label: "Verrouillée", bg: "#D1FAE5", color: "#065F46" },
  corrected: { label: "Corrigée", bg: "#FEF3C7", color: "#92400E" },
  exported: { label: "Exportée", bg: "#EDE9FE", color: "#5B21B6" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function ControleRegistrePage() {
  const { token } = useParams();
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterUsage, setFilterUsage] = useState("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/phyto-register/share/${token}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError({ status: res.status, message: err?.detail || "Lien invalide" });
        setPayload(null);
      } else {
        setPayload(await res.json());
      }
    } catch (e) {
      setError({ status: 0, message: "Impossible de contacter le serveur" });
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const entries = payload?.entries || [];
  const share = payload?.share;

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (filterStatus !== "all" && e.status !== filterStatus) return false;
      if (filterUsage !== "all" && e.usage_type !== filterUsage) return false;
      if (search) {
        const s = search.toLowerCase();
        const hay = [
          e.product?.commercial_name,
          e.product?.amm_number,
          e.crop?.crop_text,
          e.timing?.use_date,
          e.location?.parcel_name,
          e.treated_seed_sowing?.seed_treatment_product_name,
          e.crop?.variety,
          USAGE_LABELS[e.usage_type],
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [entries, filterStatus, filterUsage, search]);

  const downloadCsv = () => {
    window.location.href = `${API_BASE}/phyto-register/share/${token}/export/csv`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center" data-testid="controle-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-emerald-700 mb-2" />
          <p className="text-sm text-gray-600">Chargement du registre…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4" data-testid="controle-error">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 border-t-4 border-red-500">
          <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-center text-gray-900 mb-2">
            {error.status === 410 ? "Lien expiré ou révoqué" : (error.status === 404 ? "Lien de contrôle invalide" : "Erreur d'accès")}
          </h1>
          <p className="text-sm text-gray-600 text-center">{error.message}</p>
          <p className="text-xs text-gray-500 text-center mt-4">Demandez au détenteur de l'exploitation de générer un nouveau lien de contrôle.</p>
        </div>
      </div>
    );
  }

  const expiryDate = share?.expires_at ? new Date(share.expires_at) : null;
  const hoursLeft = expiryDate ? Math.max(0, Math.round((expiryDate.getTime() - Date.now()) / 3600000)) : 0;

  return (
    <div className="min-h-screen bg-slate-100 relative" data-testid="controle-registre-page">
      {/* Watermark filigrane */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 flex items-center justify-center opacity-[0.04] select-none"
        style={{ zIndex: 0 }}
      >
        <span className="text-[120px] font-black uppercase tracking-widest -rotate-12 text-emerald-900">
          Mode Contrôle
        </span>
      </div>

      <div className="relative" style={{ zIndex: 1 }}>
        {/* Bandeau officiel */}
        <div className="bg-gradient-to-r from-emerald-800 to-emerald-700 text-white shadow-md" data-testid="controle-header">
          <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-6 h-6" />
              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-90">Véyèr CIEL — Registre phytosanitaire</div>
                <div className="text-base font-bold leading-tight">Mode contrôle DAAF / SRAL</div>
              </div>
            </div>
            <div className="flex-1" />
            <div className="flex items-center gap-1.5 text-[11px] bg-white/10 rounded-full px-3 py-1">
              <Lock className="w-3 h-3" />
              <span>Accès lecture seule</span>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] bg-white/10 rounded-full px-3 py-1" data-testid="controle-expiry">
              <Clock className="w-3 h-3" />
              <span>Expire dans {hoursLeft} h</span>
            </div>
          </div>
        </div>

        {/* Bloc détenteur */}
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-testid="controle-holder">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                <Building2 className="w-5 h-5 text-emerald-700" />
                <div>
                  <div className="text-[10px] uppercase font-semibold text-gray-500">Exploitation détentrice</div>
                  <div className="text-sm font-bold text-gray-900">{share?.business_name || "—"}</div>
                  <div className="text-[11px] font-mono text-gray-600">SIRET {share?.siret_holder || "—"}</div>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-semibold text-gray-500">Portée du lien</div>
                <div className="text-sm text-gray-900">{share?.parcel_id ? `Parcelle ${share.parcel_id}` : "Toutes les parcelles"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-semibold text-gray-500">Généré le</div>
                <div className="text-sm text-gray-900">{fmtDate(share?.created_at)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-semibold text-gray-500">Expire le</div>
                <div className="text-sm text-gray-900">{fmtDate(share?.expires_at)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-semibold text-gray-500">Accès n°</div>
                <div className="text-sm font-bold text-gray-900" data-testid="controle-access-count">{share?.access_count || 1}</div>
              </div>
            </div>
            {share?.controller_note && (
              <div className="mt-3 text-[11px] text-gray-600 border-t pt-2">
                <span className="font-semibold">Note du détenteur :</span> {share.controller_note}
              </div>
            )}
            {share?.include_drafts && (
              <div className="mt-2 text-[11px] flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                <AlertTriangle className="w-3 h-3" /> Ce lien inclut les <b>brouillons non validés</b> à la demande explicite du détenteur.
              </div>
            )}
          </div>

          {/* Filtres */}
          <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200 p-3 flex flex-wrap gap-2 items-center" data-testid="controle-filters">
            <Input
              placeholder="Rechercher (produit, AMM, culture…)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 text-sm flex-1 min-w-[200px]"
              data-testid="controle-search"
            />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-9 text-xs w-44" data-testid="controle-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterUsage} onValueChange={setFilterUsage}>
              <SelectTrigger className="h-9 text-xs w-56" data-testid="controle-filter-usage"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types d'utilisation</SelectItem>
                {Object.entries(USAGE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-9 text-xs" onClick={downloadCsv} data-testid="controle-download-csv">
              <FileDown className="w-3.5 h-3.5 mr-1" /> Export CSV
            </Button>
            <Button size="sm" variant="ghost" className="h-9 text-xs" onClick={load} data-testid="controle-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualiser
            </Button>
          </div>

          {/* Compteurs */}
          <div className="mt-3 flex items-center gap-4 text-xs text-gray-600">
            <span data-testid="controle-total">
              <b className="text-gray-900">{filtered.length}</b> intervention{filtered.length > 1 ? "s" : ""} affichée{filtered.length > 1 ? "s" : ""}
              {filtered.length !== entries.length && <span className="opacity-70"> sur {entries.length}</span>}
            </span>
            <span className="opacity-60">·</span>
            <span>Généré le {fmtDate(payload?.generated_at)}</span>
          </div>

          {/* Table */}
          <div className="mt-3 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-testid="controle-table">
            {filtered.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                <Eye className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Aucune intervention à afficher</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead className="bg-gray-50 text-[10px] uppercase text-gray-600 font-semibold">
                    <tr>
                      <th className="text-left px-3 py-2">N° interv.</th>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Statut</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-left px-3 py-2">Produit / AMM</th>
                      <th className="text-left px-3 py-2">Culture</th>
                      <th className="text-right px-3 py-2">Dose</th>
                      <th className="text-right px-3 py-2">Quantité</th>
                      <th className="text-right px-3 py-2">Surface/vol.</th>
                      <th className="text-left px-3 py-2">Parcelle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((e, i) => {
                      const st = STATUS_META[e.status] || STATUS_META.draft;
                      const mix = (e.pass_info?.size || 1) > 1;
                      const sameAsPrev = i > 0 && !!e.intervention_n && filtered[i - 1].intervention_n === e.intervention_n;
                      return (
                        <tr key={e.entry_id} className={`${sameAsPrev ? "" : "border-t border-gray-100"} hover:bg-emerald-50/40 ${mix ? "bg-emerald-50/30" : ""}`} data-testid={`controle-row-${e.entry_id}`}>
                          <td className="px-3 py-2 whitespace-nowrap font-mono text-gray-600" style={mix ? { borderLeft: "3px solid #2D6A4F" } : undefined}>{e.intervention_n ?? "—"}</td>
                          <td className="px-3 py-2 whitespace-nowrap font-mono">{e.timing?.use_date || "—"}</td>
                          <td className="px-3 py-2">
                            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                            {e.version > 1 && <span className="ml-1 text-[10px] text-gray-500">v{e.version}</span>}
                          </td>
                          <td className="px-3 py-2 text-[11px]">{USAGE_LABELS[e.usage_type] || e.usage_type}</td>
                          <td className="px-3 py-2">
                            <div className="font-semibold text-gray-900">{isEvent(e)
                              ? `${USAGE_LABELS[e.usage_type]} · variété ${e.crop?.variety || "?"} · ${e.cultivation?.quantity_value ?? "?"} ${e.cultivation?.quantity_unit || ""}`
                              : (e.product?.commercial_name || e.treated_seed_sowing?.seed_treatment_product_name || "—")}</div>
                            {e.product?.amm_number && <div className="text-[10px] text-gray-500 font-mono">AMM {e.product.amm_number}</div>}
                            {mix && (
                              <div className="text-[10px] font-semibold text-emerald-800" data-testid={`controle-mix-${e.entry_id}`}>
                                Application en mélange ({e.pass_info.index}/{e.pass_info.size}) : {(e.pass_info.products || []).join(" + ")}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2">{e.crop?.crop_text || "—"}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">{e.dose?.value != null ? `${e.dose.value} ${e.dose.unit || ""}` : "—"}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">{e.quantity?.product_quantity_value != null ? `${e.quantity.product_quantity_value} ${e.quantity.product_quantity_unit || ""}` : "—"}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">{e.treated_unit?.value != null ? `${e.treated_unit.value} ${e.treated_unit.unit || ""}` : "—"}</td>
                          <td className="px-3 py-2 text-[11px] text-gray-600">{e.location?.parcel_name || e.parcel_id || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer légal */}
          <div className="mt-4 mb-8 text-[11px] text-gray-500 bg-white/60 border border-gray-200 rounded-lg p-3">
            <p>
              Ce document est une <b>vue en lecture seule</b> du registre phytosanitaire numérique tenu par l'exploitation ci-dessus,
              généré par Véyèr CIEL conformément aux exigences de l'arrêté du 24 décembre 2025. Le contenu est un extrait à
              l'instant du chargement — toute correction ultérieure fait l'objet d'un versioning historisé (statut « Corrigée »).
              L'accès à ce lien est journalisé par le détenteur.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
